import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Input,
  FormGroup,
  Badge,
  Button,
} from "reactstrap";
import DataTable from "react-data-table-component";
import { Triangle } from "react-loader-spinner";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import StockSummaryServices from "../../Services/QRCodeServices/StockSummaryServices/StockSummaryServices";
import PrintableTable from "./Components/PrintableTable";
import PrintHeader from "../../Components/PrintHeader/PrintHeader";

function StockSummary() {
  const [stockItems, setStockItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
      },
    },
    rows: {
      style: {
        fontSize: "0.9rem",
      },
    },
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (row.id === "total-row" ? "" : index + 1),
      sortable: true,
      width: "80px",
    },
    {
      name: "Menu Item Name",
      selector: (row) => row.menuItemName,
      sortable: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.menuItemName}
        </div>
      ),
    },
    {
      name: "Purchase Count",
      selector: (row) => row.purchaseCount,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.purchaseCount}
        </div>
      ),
    },
    {
      name: "Sales Count",
      selector: (row) => row.menuOrderDetailsCount,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.menuOrderDetailsCount}
        </div>
      ),
    },
    {
      name: "Remaining Quantity",
      selector: (row) => row.remainingQty,
      sortable: true,
      right: true,
      cell: (row) => (
        <div className={row.id === "total-row" ? "fw-bold" : ""}>
          {row.remainingQty}
        </div>
      ),
    },
    {
      name: "Stock Status",
      selector: (row) =>
        row.id === "total-row" ? "" : (
          <Badge color={row.isStockLow ? "danger" : "success"}>
            {row.isStockLow ? "Low Stock" : "In Stock"}
          </Badge>
        ),
      sortable: true,
      center: true,
    },
  ];

  async function fetchStockSummary() {
    try {
      setLoading(true);
      const fetchedItems = await StockSummaryServices.getList();
      setStockItems(fetchedItems);
      setFilteredItems(fetchedItems);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching stock summary:", err);
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchStockSummary();
  }, []);

  useEffect(() => {
    let filtered = stockItems;

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.menuItemName?.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    setFilteredItems(filtered);
  }, [searchTerm, stockItems]);

  const handlePrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      window.print();
      setIsPrinting(false);
    }, 500);
  };

  const totalPurchaseCount = filteredItems.reduce(
    (sum, row) => sum + row.purchaseCount,
    0
  );
  const totalSalesCount = filteredItems.reduce(
    (sum, row) => sum + row.menuOrderDetailsCount,
    0
  );
  const totalRemainingQty = filteredItems.reduce(
    (sum, row) => sum + row.remainingQty,
    0
  );

  const totalRow = {
    menuItemName: "Total",
    purchaseCount: totalPurchaseCount,
    menuOrderDetailsCount: totalSalesCount,
    remainingQty: totalRemainingQty,
    isStockLow: false,
    id: "total-row",
  };

  const tableData =
    filteredItems.length > 0 ? [...filteredItems, totalRow] : filteredItems;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Stock Summary Report" pageTitle="Stock Summary" />
          <Row>
            <Col>
              <Card>
                <CardBody>
                  <PrintHeader reportTitle="Stock Summary Report" />
                  <div className="d-print-none">
                    <div className="d-flex flex-wrap gap-2">
                      <FormGroup className="col-lg-3">
                        <Input
                          type="text"
                          placeholder="Search by Menu Item Name"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </FormGroup>
                      <div className="ms-auto">
                        {filteredItems.length > 0 && (
                          <Button
                            color="outline-secondary"
                            className="py-2 px-3 d-flex align-items-center shadow-sm"
                            onClick={handlePrint}
                          >
                            <i className="ri-printer-line fs-16"></i>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
              <Card>
                <CardBody>
                  {isPrinting ? (
                    <PrintableTable stockItems={filteredItems} />
                  ) : (
                    <DataTable
                      columns={columns}
                      data={tableData}
                      customStyles={customStyles}
                      responsive
                      striped
                      highlightOnHover
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      progressPending={loading}
                      progressComponent={
                        <div className="my-3">
                          <Triangle
                            visible={true}
                            height="80"
                            width="80"
                            color="#5B71B9"
                            ariaLabel="triangle-loading"
                          />
                          <h5 className="mt-1">Loading...</h5>
                        </div>
                      }
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default StockSummary;